/* Naughty Hut cart drawer: honest loading state + modal dialog behaviour (QA fix batch A, 2026-09-28).
 *
 * 1. Loading (FE-01 / DQA-03). The Impulse drawer opens on "ajaxProduct:added" and re-reads the cart
 *    (/cart?view=ajax) afterwards, so for 1-3 s it showed the pre-add cart: "Your cart is currently empty".
 *    Every POST to /cart/add (any script: buy box, cards, search, Nova) now puts the drawer in a busy
 *    state from the moment the request starts: the empty state is hidden, a skeleton line reads
 *    "Adding…", then the added product from the /cart/add.js response, until the re-rendered cart lands.
 * 2. Dialog (A11Y-01). role="dialog" + aria-modal + aria-labelledby live in the markup
 *    (snippets/cart-drawer.liquid). Here: inert while closed, focus to the title on open, Tab/Shift+Tab
 *    cycle inside, Escape closes, focus goes back to whatever opened it (Add to bag, Bag, cart icon).
 * 3. Focus after quantity changes; 4. keyboard-only title ring; 5. preview-bar lift (redesign 2026-09-29).
 * Styles: assets/nh-i-cart.css. */
(() => {
  'use strict';
  if (window.__nhCartDrawer) return;
  window.__nhCartDrawer = true;

  const drawer = document.getElementById('CartDrawer');
  if (!drawer) return;
  const doc = document;
  const pending = drawer.querySelector('[data-nhcd-pending]');
  const status = drawer.querySelector('[data-nhcd-status]');
  const products = drawer.querySelector('[data-products]');
  const title = drawer.querySelector('#CartDrawerTitle');
  const money = (cents) => {
    try { return window.theme.Currency.formatMoney(cents, window.theme.settings.moneyFormat); } catch (e) { return `$${(cents / 100).toFixed(2)}`; }
  };
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const say = (msg) => { if (!status) return; status.textContent = ''; requestAnimationFrame(() => { status.textContent = msg; }); };

  /* ---------- 1. busy state around every add ---------- */
  let busy = false;
  let addedAt = 0;
  let safety = 0;
  const skeleton = () => '<div class="nhcd-line"><span class="nhcd-thumb nhcd-sk"></span><span class="nhcd-text"><span class="nhcd-sk nhcd-sk--w70"></span><span class="nhcd-sk nhcd-sk--w40"></span></span></div><p class="nhcd-label">Adding to your bag…</p>';
  const setBusy = (on) => {
    busy = on;
    drawer.classList.toggle('nhcd-busy', on);
    drawer.querySelector('form')?.setAttribute('aria-busy', String(on));
    if (pending) pending.hidden = !on;
    clearTimeout(safety);
    if (on) safety = setTimeout(() => setBusy(false), 9000); // never stick if the re-render is lost
  };
  const startAdd = () => {
    addedAt = 0;
    if (pending) pending.innerHTML = skeleton();
    setBusy(true);
    say('Adding to your bag');
  };
  const showAdded = (data) => {
    addedAt = performance.now();
    const items = Array.isArray(data?.items) ? data.items : data && data.id ? [data] : [];
    if (!pending || !items.length) return;
    pending.innerHTML = items.slice(0, 3).map((it) => {
      const img = it.featured_image?.url || it.image || '';
      const src = img ? `${img}${img.includes('?') ? '&' : '?'}width=160` : '';
      const variant = it.variant_title && !/default title/i.test(it.variant_title) ? `<span class="nhcd-variant">${esc(it.variant_title)}</span>` : '';
      return `<div class="nhcd-line">${src ? `<img class="nhcd-thumb" src="${esc(src)}" alt="" width="64" height="64">` : '<span class="nhcd-thumb nhcd-sk"></span>'}<span class="nhcd-text"><span class="nhcd-title">${esc(it.product_title || it.title)}</span>${variant}<span class="nhcd-price">${it.quantity > 1 ? `${it.quantity} × ` : ''}${money(it.final_price ?? it.price)}</span></span></div>`;
    }).join('') + '<p class="nhcd-label"><svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false"><path d="M3 8.5 6.5 12 13 4.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>Added. Updating your bag…</p>';
    say(`Added ${items[0].product_title || items[0].title || 'item'} to your bag`);
  };
  const isAdd = (input, init) => {
    try {
      const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input?.url;
      const method = (init?.method || (typeof input === 'object' && input?.method) || 'GET').toUpperCase();
      return method === 'POST' && /\/cart\/add(\.js)?(\?|$)/.test(new URL(url, location.href).pathname + '?');
    } catch (e) { return false; }
  };
  const nativeFetch = window.fetch;
  if (typeof nativeFetch === 'function') {
    window.fetch = function (input, init) {
      if (!isAdd(input, init)) return nativeFetch.apply(this, arguments);
      startAdd();
      return nativeFetch.apply(this, arguments).then((res) => {
        if (!res.ok) { setBusy(false); return res; }
        res.clone().json().then(showAdded, () => { addedAt = performance.now(); });
        return res;
      }, (err) => { setBusy(false); throw err; });
    };
  }
  // The theme re-renders [data-products] from /cart?view=ajax; the first render after the add answered ends the wait.
  if (products && 'MutationObserver' in window) {
    new MutationObserver(() => {
      if (!busy || !addedAt) return;
      setBusy(false);
      const n = Number(products.querySelector('[data-count]')?.dataset.count || 0);
      if (n) say(`Your bag has ${n} ${n === 1 ? 'item' : 'items'}`);
    }).observe(products, { childList: true });
  }

  /* ---------- 2. modal dialog ---------- */
  const OPEN = 'drawer--is-open';
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  let opener = null;
  let lastOutside = null;
  const isOpen = () => drawer.classList.contains(OPEN);
  const focusables = () => [...drawer.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null || el === doc.activeElement);
  doc.addEventListener('focusin', (e) => {
    if (drawer.contains(e.target)) return;
    if (isOpen()) { (title || focusables()[0])?.focus({ preventScroll: true }); return; } // focus never escapes the open dialog
    lastOutside = e.target;
  });
  // Fix batch 2026-09-29 (P2-05): the dock's Bag relays its tap to the header cart icon, so the
  // header icon looked like the opener and got focus back on close. Remember the real control.
  let tapped = null;
  let tappedAt = 0;
  doc.addEventListener('click', (e) => {
    const t = e.target instanceof Element ? e.target.closest('[data-nhk-bag], .js-drawer-open-cart') : null;
    if (!t || drawer.contains(t)) return;
    if (t.matches('.js-drawer-open-cart') && tapped && performance.now() - tappedAt < 400) return; // the relayed click
    tapped = t;
    tappedAt = performance.now();
  }, true);
  const onOpen = () => {
    drawer.inert = false;
    drawer.removeAttribute('aria-hidden');
    opener = doc.activeElement && doc.activeElement !== doc.body && !drawer.contains(doc.activeElement) ? doc.activeElement : lastOutside;
    if (tapped && performance.now() - tappedAt < 1500) opener = tapped;
    tapped = null;
    measureLift();
    requestAnimationFrame(() => title?.focus({ preventScroll: true }));
  };
  const onClose = () => {
    drawer.inert = true;
    drawer.setAttribute('aria-hidden', 'true');
    const back = opener;
    opener = null;
    // Impulse blurs on close and restores only a clicked opener after 500 ms; step in only if focus is lost.
    setTimeout(() => {
      const ae = doc.activeElement;
      const relayed = ae && back && ae !== back && ae.matches?.('.js-drawer-open-cart') && back.matches?.('[data-nhk-bag]');
      if (!relayed && ae && ae !== doc.body && ae !== doc.documentElement && !drawer.contains(ae)) return;
      let target = back && back.isConnected && back.offsetParent !== null ? back : null;
      if (!target) target = [...doc.querySelectorAll('.js-drawer-open-cart, [data-nhk-bag]')].find((a) => a.offsetParent !== null) || null;
      target?.focus({ preventScroll: true });
    }, 540);
  };
  let was = isOpen();
  if (!was) { drawer.inert = true; drawer.setAttribute('aria-hidden', 'true'); }
  new MutationObserver(() => {
    const now = isOpen();
    if (now === was) return;
    was = now;
    if (now) onOpen(); else onClose();
  }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
  /* ---------- 3. Focus after quantity changes (FE-F12) ----------
     Remove in place + undo now lives in assets/nh-cart-boost.js (shared with /cart). After any rebuild the old
     nodes are gone, so focus goes back to the matching stepper button (or the title). */
  let refocus = null; // { key, sel }
  const lineKey = (el) => el.closest('[data-key]')?.dataset.key || null;
  const restoreFocus = () => {
    if (!refocus || !isOpen()) { refocus = null; return; }
    const { key, sel } = refocus;
    refocus = null;
    const line = key ? [...drawer.querySelectorAll('[data-nhb-line]')].find((n) => n.dataset.key === key) : null;
    const target = (line && sel && line.querySelector(sel)) || (line && line.querySelector('.js-qty__adjust--plus')) || title;
    target?.focus({ preventScroll: true });
  };
  if (products && 'MutationObserver' in window) {
    new MutationObserver(() => { if (refocus) requestAnimationFrame(restoreFocus); }).observe(products, { childList: true });
  }
  drawer.addEventListener('click', (e) => {
    const adj = e.target instanceof Element ? e.target.closest('.js-qty__adjust') : null;
    if (adj && !adj.hasAttribute('data-nhb-remove-one')) {
      refocus = { key: lineKey(adj), sel: adj.classList.contains('js-qty__adjust--plus') ? '.js-qty__adjust--plus' : '.js-qty__adjust--minus:not(:disabled)' };
    }
  }, true);

  /* ---------- 4. Focus ring only for keyboard users (owner 2026-09-29: boxed "Your cart" title) ----------
     Opening the drawer moves focus to the title programmatically, and Safari/Chrome can paint :focus-visible
     for that even after a tap. The ring on the title is drawn only while .nhb-kbd is set, i.e. after a
     keyboard interaction; any pointer interaction clears it. Controls keep their own :focus-visible rings. */
  const KBD = new Set(['Tab', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter', ' ', 'Escape', 'Home', 'End']);
  doc.addEventListener('keydown', (e) => { if (KBD.has(e.key)) drawer.classList.add('nhb-kbd'); }, true);
  doc.addEventListener('pointerdown', () => drawer.classList.remove('nhb-kbd'), true);

  /* ---------- 5. Keep the footer above the Shopify preview bar ----------
     The preview bar is a bottom-docked top-layer frame customers never see; when it is there the footer is
     padded by its height (--nhb-lift), the same measurement as the dock (assets/nh-dock.js). */
  const PB_SEL = '#PBarNextFrameWrapper, #preview-bar-iframe';
  const measureLift = () => {
    const el = doc.querySelector(PB_SEL);
    let lift = 0;
    if (el && el.isConnected) {
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight;
      if (r.height > 0 && r.width > 0 && r.bottom >= vh - 4 && r.top > vh * 0.5) lift = Math.min(Math.round(vh - r.top), 140);
    }
    drawer.style.setProperty('--nhb-lift', `${lift}px`);
  };
  measureLift();
  window.addEventListener('resize', measureLift, { passive: true });
  new MutationObserver(measureLift).observe(doc.body, { childList: true });

  doc.addEventListener('keydown', (e) => {
    if (!isOpen()) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      doc.dispatchEvent(new CustomEvent('cart:close'));
      return;
    }
    if (e.key !== 'Tab') return;
    const f = focusables();
    if (!f.length) { e.preventDefault(); return; }
    const first = f[0];
    const last = f[f.length - 1];
    const ae = doc.activeElement;
    if (e.shiftKey && (ae === first || ae === title || !drawer.contains(ae))) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && (ae === last || !drawer.contains(ae))) { e.preventDefault(); first.focus(); }
  }, true);
})();
